import { Observable, throwError } from 'rxjs';
import { computed, inject, Injectable, signal } from '@angular/core';
import { Router } from '@angular/router';

import { catchError, map, tap } from 'rxjs/operators';

import { AuthService as AuthApiService } from '../../app/api/api/auth.service';
import { AuthResponse } from '../../app/api/model/authResponse';
import { LoginDto } from '../../app/api/model/loginDto';
import { UserService } from '../domain/user/user.service';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private authState = signal<boolean>(false);
  private initState = signal<boolean>(false);

  public readonly isAuthenticated = this.authState.asReadonly();
  public readonly isInitialized = this.initState.asReadonly();
  public readonly isAuthReady = computed(() => this.isInitialized() && this.isAuthenticated());

  redirectUrl: string | null = null;
  private router = inject(Router);
  private authApiService = inject(AuthApiService);
  private userService = inject(UserService);

  constructor() {
    this.initializeAuth();
  }

  private initializeAuth(): void {
    const token = this.getToken();

    if (!token) {
      this.authState.set(false);
      this.initState.set(true);
      return;
    }

    this.authApiService
      .authControllerMe(`Bearer ${token}`)
      .pipe(map((response) => response.data!))
      .subscribe({
        next: (response: AuthResponse) => {
          if (response.accessToken) {
            this.setToken(response.accessToken);
            this.userService.setUser(response.user);
            this.authState.set(true);
            this.initState.set(true);

            if (this.redirectUrl) {
              this.router.navigateByUrl(this.redirectUrl);
              this.redirectUrl = null;
            }
          }
        },
        error: (error) => {
          console.error('Error during auth initialization:', error);
          this.handleAuthError();
          this.initState.set(true);
        },
      });
  }

  login(email: string, password: string): Observable<AuthResponse> {
    const loginDto: LoginDto = {
      email,
      password,
    };

    return this.authApiService.authControllerLogin(loginDto).pipe(
      map((response) => response.data!),
      tap((response: AuthResponse) => {
        if (response?.accessToken) {
          this.setToken(response.accessToken);
          this.userService.setUser(response.user);
          this.authState.set(true);
          this.initState.set(true);
        }
      }),
      catchError((error) => {
        this.handleAuthError();
        return throwError(() => error);
      }),
    );
  }

  logout(): void {
    this.authApiService
      .authControllerLogout()
      .pipe(
        tap(() => {
          this.authState.set(false);
          this.initState.set(true);
          this.clearToken();
          this.userService.setUser(null);
          this.router.navigate(['/login']);
        }),
        catchError((error) => {
          console.error('Logout failed:', error);
          this.authState.set(false);
          this.initState.set(true);
          this.clearToken();
          this.userService.setUser(null);
          this.router.navigate(['/login']);
          return throwError(() => error);
        }),
      )
      .subscribe();
  }

  private getToken(): string | null {
    return localStorage.getItem('gateway_auth_token');
  }

  private setToken(token: string): void {
    localStorage.setItem('gateway_auth_token', token);
  }

  private clearToken(): void {
    localStorage.removeItem('gateway_auth_token');
  }

  private handleAuthError(): void {
    this.authState.set(false);
    this.clearToken();
    this.userService.setUser(null);
  }
}
